import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CTAButton, Divider, Eyebrow, Reveal, Section, SiteFooter } from "./primitives";
import { SPIRAL_URL, LASER_URL } from "./constants";

const CTA_LABEL = "Book Your Spiral Dynamics Analysis — $147";

const INCLUDED = [
  {
    title: "The AI Assessment",
    body: "A purpose-built conversational assessment across eight domains of your inner and outer world — designed specifically to generate the most accurate developmental picture possible. You complete it in your own time before the session. Both you and Aaron receive the preliminary report.",
  },
  {
    title: "The Live Session",
    body: "60-70 minutes with Aaron to go through it together. Validating what landed, challenging what didn't, going deeper into what actually came up. Expect depth, directness and genuine clarity — this is where the real work happens.",
  },
  {
    title: "Your Developmental Report — Yours To Keep",
    body: "A clear picture of where you're currently operating across the key areas of your life — inner and outer. Your growth edges named honestly. The specific behavioural strategies and modalities calibrated to your level of development, not what's trending or what worked for someone at a different stage. Everything you need to stop circling and start moving — grounded in what actually came up across both the assessment and the session.",
  },
  {
    title: "A Path Forward — With or Without Me",
    body: "This isn't a sales call in disguise. You'll leave with something genuinely valuable regardless of what you decide to do next. If we're a fit to work together, we'll know. If not, you still walk away with more clarity than you came in with.",
  },
];

const FOR_YOU = [
  "You've been doing some work on yourself but feel like you keep hitting the same walls.",
  "You're at a crossroads — personally, relationally, professionally — and need real clarity on what's actually happening and what to do next.",
  "You've had an awakening or a significant life shift and can't quite find your footing again.",
  "You want an honest outside perspective from someone who can see your patterns clearly and tell you the truth about what they mean.",
  "You're considering working with Aaron and want to experience the work before committing to a full container.",
];

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      data-testid="spiral-nav"
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? "backdrop-blur-md bg-[#0D1B2A]/85 border-b border-[#C9A84C]/15 py-4"
          : "bg-transparent py-6"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 flex items-center justify-between gap-4">
        <Link
          to="/"
          data-testid="spiral-nav-monogram"
          className="font-serif text-2xl tracking-[0.3em] text-[#C9A84C]"
        >
          AC
        </Link>
        <a
          href={SPIRAL_URL}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="spiral-nav-cta"
          className="text-[0.6rem] sm:text-xs tracking-[0.2em] uppercase text-[#F5F0E8] hover:text-[#C9A84C] transition-colors duration-300 border-b border-transparent hover:border-[#C9A84C] pb-1 text-right"
        >
          Book Your Spiral Dynamics Analysis — $147
        </a>
      </div>
    </header>
  );
};

const Hero = () => (
  <section
    id="spiral-hero"
    className="relative min-h-screen w-full flex items-center justify-center overflow-hidden bg-[#0D1B2A]"
  >
    <div className="absolute inset-0 bg-[#0D1B2A]" aria-hidden="true">
      {/* faint spiral/compass texture */}
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[120vmin] h-[120vmin] opacity-[0.06]">
        {[0, 1, 2, 3, 4, 5].map((i) => (
          <div
            key={i}
            className="absolute rounded-full border border-[#C9A84C]"
            style={{
              inset: `${i * 8}%`,
            }}
          />
        ))}
        <div className="absolute inset-0 bg-[conic-gradient(from_0deg,transparent,rgba(201,168,76,0.5),transparent_35%)] rounded-full" />
      </div>
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_60%_at_50%_112%,rgba(184,84,30,0.22),transparent_68%)]" />
      <div className="absolute inset-0 bg-gradient-to-b from-[#0D1B2A] via-[#0D1B2A]/70 to-[#0D1B2A]/80" />
    </div>

    <div className="relative z-10 max-w-3xl mx-auto px-6 text-center">
      <Reveal>
        <Eyebrow className="mb-8">The First Step</Eyebrow>
        <h1 className="font-serif font-medium uppercase text-[#C9A84C] leading-[0.95] tracking-tight text-5xl sm:text-6xl md:text-7xl lg:text-[5.5rem]">
          Your Spiral Dynamics
          <br />
          Analysis
        </h1>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-8 font-serif italic text-xl sm:text-2xl md:text-3xl text-[#F5F0E8] leading-snug">
          Clarity. Direction. A path forward — with or without me.
        </p>
      </Reveal>
      <Reveal delay={0.3}>
        <div className="mt-12">
          <CTAButton testId="spiral-hero-cta" href={SPIRAL_URL}>
            {CTA_LABEL}
          </CTAButton>
        </div>
      </Reveal>
    </div>
  </section>
);

const SpiralPage = () => {
  return (
    <div className="bg-[#0D1B2A] font-sans antialiased selection:bg-[#C9A84C]/30 selection:text-[#F5F0E8]">
      <Nav />
      <Hero />

      {/* SECTION 1 — WHAT THIS IS */}
      <Section variant="light" id="what-this-is">
        <Reveal>
          <div className="space-y-7 text-center text-[#1A2535]/85 font-sans font-light text-base sm:text-lg leading-relaxed">
            <p>
              Most men come to this work knowing something is off but not being
              able to name it precisely. They've read the books, done some work
              on themselves, maybe tried therapy — and they still feel like
              they're circling. Making progress in one area, losing ground in
              another. Spinning.
            </p>
            <p className="font-serif italic text-2xl sm:text-3xl text-[#1A2535] !mt-10">
              The Spiral Dynamics Analysis cuts through that.
            </p>
            <p>
              Before your session, you'll complete a Spiral Dynamics assessment
              with a purpose built A.I tool — it'll be a conversational deep-dive
              across eight key domains of your inner and outer world.
              Relationships, purpose, emotional patterns, identity, how you
              handle power and structure, what drives your growth. It's been
              specifically designed to generate the most accurate developmental
              picture possible before we get on a call.
            </p>
            <p>
              You'll receive your preliminary report before the session. I
              receive it at the same time.
            </p>
            <p className="font-serif italic text-xl sm:text-2xl text-[#1A2535]">
              Then we meet.
            </p>
            <p>
              In a single 60-70 minute live session, we go through it together —
              validating what landed, challenging what didn't, and going deeper
              into what actually came up. Using established developmental
              frameworks, I'll identify precisely which stage of growth you're
              operating from, what's keeping you there, and — critically — what
              would actually move you forward. Not what worked for someone else.
              What works for you, at your level, right now.
            </p>
            <p>
              What you walk away with is a full written document: your
              developmental map, your key growth edges, and the specific
              strategies and modalities most likely to create real movement in
              your life — this level of clarity will ACTUALLY move the needle
              rather than keep you spinning in circles.
            </p>
          </div>
        </Reveal>
      </Section>

      {/* SECTION 2 — WHAT'S INCLUDED */}
      <Section variant="dark" id="whats-included">
        <Reveal className="text-center">
          <Eyebrow>What's Included</Eyebrow>
          <Divider />
        </Reveal>
        <div className="mt-8 space-y-16 sm:space-y-20">
          {INCLUDED.map((item, i) => (
            <Reveal key={i} delay={i * 0.05}>
              <div className="text-center" data-testid={`included-${i}`}>
                <h3 className="font-serif text-[#C9A84C] text-2xl sm:text-3xl leading-tight mb-4">
                  {item.title}
                </h3>
                <p className="font-sans font-light text-[#F5F0E8]/85 text-base sm:text-lg leading-relaxed max-w-xl mx-auto">
                  {item.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </Section>

      {/* SECTION 3 — THE INVESTMENT */}
      <Section variant="light" id="the-investment">
        <Reveal className="text-center">
          <Eyebrow>The Investment</Eyebrow>
          <Divider />
        </Reveal>
        <Reveal>
          <div className="space-y-7 text-center text-[#1A2535]/85 font-sans font-light text-base sm:text-lg leading-relaxed">
            <p>
              Most people spend years — and thousands of dollars — trying things
              that don't fit where they're actually at. The wrong modality, the
              wrong book, the wrong coach, at the wrong time. Not because the tools
              are bad, but because nobody's ever mapped the territory first.
            </p>
            <p className="font-serif italic text-2xl sm:text-3xl text-[#1A2535] !mt-10">
              This does that. It takes the guesswork out.
            </p>
            <p>
              Knowing precisely where you are developmentally — and what would
              actually move you forward from that specific place — doesn't just save
              you money in the short term. It saves you time. And time is the one
              thing you can't get back.
            </p>
          </div>
        </Reveal>
        <Reveal className="text-center">
          <p className="mt-14 font-serif text-[#C9A84C] text-5xl sm:text-6xl">
            $147 AUD
          </p>
          <p className="mt-3 font-sans font-light italic text-[#1A2535]/70">
            (fully redeemable against any coaching container)
          </p>
          <p className="mt-8 max-w-xl mx-auto font-sans font-light text-[#1A2535]/85 text-base sm:text-lg leading-relaxed">
            You're not paying to be sold to. You're paying for genuine clarity —
            and getting your money back if you decide to go further.
          </p>
          <div className="mt-12">
            <CTAButton testId="spiral-investment-cta" href={SPIRAL_URL} tone="navy">
              Book Your Spiral Analysis — $147
            </CTAButton>
          </div>
        </Reveal>
      </Section>

      {/* SECTION 4 — IS THIS FOR YOU? */}
      <Section variant="dark" id="is-this-for-you">
        <Reveal className="text-center">
          <Eyebrow>Is This For You?</Eyebrow>
          <Divider />
          <p className="font-serif text-[#F5F0E8] text-2xl sm:text-3xl mb-12">
            This is for you if:
          </p>
        </Reveal>
        <Reveal>
          <ul className="space-y-7 max-w-2xl mx-auto">
            {FOR_YOU.map((point, i) => (
              <li
                key={i}
                data-testid={`for-you-${i}`}
                className="flex items-start gap-4 font-sans font-light text-[#F5F0E8]/90 text-base sm:text-lg leading-relaxed"
              >
                <span
                  aria-hidden="true"
                  className="mt-3 shrink-0 w-6 h-px bg-[#C9A84C]"
                />
                <span>{point}</span>
              </li>
            ))}
          </ul>
        </Reveal>
        <Reveal>
          <p className="mt-14 max-w-2xl mx-auto text-center font-serif italic text-xl sm:text-2xl text-[#F5F0E8]/80 leading-snug">
            This is not for you if you're looking for validation of what you
            already believe, or if you're not ready to hear something that might
            challenge your current story.
          </p>
        </Reveal>
        <Reveal className="text-center">
          <div className="mt-12">
            <CTAButton testId="spiral-foryou-cta" href={SPIRAL_URL}>
              {CTA_LABEL}
            </CTAButton>
          </div>
        </Reveal>
      </Section>

      {/* SECTION 5 — NOT READY TO COMMIT? */}
      <Section variant="light" id="not-ready" className="!py-16 sm:!py-24">
        <Reveal className="text-center">
          <Eyebrow>Not Ready Yet?</Eyebrow>
          <Divider className="!my-8" />
          <div className="max-w-xl mx-auto space-y-5 text-[#1A2535]/80 font-sans font-light text-base leading-relaxed">
            <p>
              If you're not quite ready for the full Spiral Dynamics Analysis
              but
              something on this page has resonated — book a free 15-minute laser
              call.
            </p>
            <p>
              No pitch. No pressure. Just a quick conversation to see if this
              work is the right fit for where you're at.
            </p>
          </div>
          <div className="mt-9">
            <CTAButton
              testId="spiral-laser-cta"
              href={LASER_URL}
              tone="gold"
              size="sm"
            >
              Book a Free 15-Min Laser Call
            </CTAButton>
          </div>
        </Reveal>
      </Section>

      <SiteFooter />
    </div>
  );
};

export default SpiralPage;
